import { useMemo, useState } from "react";
import { Modal, Upload, Button, Table, Typography, Alert, Space, Tag } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { postApi } from "redux/apis";
import { toastText } from "utils/utils";

const { Dragger } = Upload;
const { Text, Title } = Typography;

interface MismatchRow {
  sku: string;
  csvLabel: string;
  itemId: number | null;
  fullName: string | null;
  csvFtzAvailable: number | null;
  csvOnsiteAvailable: number | null;
  currentFtzAvailable: number | null;
  currentOnsiteAvailable: number | null;
  ftzChanged: boolean;
  onsiteChanged: boolean;
  matchStatus: string;
}

interface NotPartRow {
  sku: string;
  csvLabel: string;
  csvFtzAvailable: number | null;
  csvOnsiteAvailable: number | null;
  reason: string;
}

interface ReviewSummary {
  uniqueItems: number;
  matchedInDatabase: number;
  mismatchedQtyCount: number;
  notPartOfFtzOnsiteCount: number;
  unchanged: number;
}

interface ReviewResult {
  summary: ReviewSummary;
  mismatchedQty: MismatchRow[];
  notPartOfFtzOnsite: NotPartRow[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const SiteQtyReviewModal = ({ isOpen, onClose }: Props) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [result, setResult] = useState<ReviewResult | null>(null);

  const mismatchColumns = useMemo(
    () => [
      { title: "SKU", dataIndex: "sku", key: "sku", width: 160 },
      { title: "CSV Label", dataIndex: "csvLabel", key: "csvLabel", ellipsis: true },
      {
        title: "FTZ CSV",
        dataIndex: "csvFtzAvailable",
        key: "csvFtzAvailable",
        width: 90,
        render: (v: number | null, r: MismatchRow) =>
          r.ftzChanged ? <Tag color="orange">{v ?? "-"}</Tag> : v ?? "-",
      },
      {
        title: "FTZ Current",
        dataIndex: "currentFtzAvailable",
        key: "currentFtzAvailable",
        width: 100,
        render: (v: number | null) => v ?? "-",
      },
      {
        title: "Onsite CSV",
        dataIndex: "csvOnsiteAvailable",
        key: "csvOnsiteAvailable",
        width: 100,
        render: (v: number | null, r: MismatchRow) =>
          r.onsiteChanged ? <Tag color="orange">{v ?? "-"}</Tag> : v ?? "-",
      },
      {
        title: "Onsite Current",
        dataIndex: "currentOnsiteAvailable",
        key: "currentOnsiteAvailable",
        width: 110,
        render: (v: number | null) => v ?? "-",
      },
    ],
    []
  );

  const notPartColumns = useMemo(
    () => [
      { title: "SKU", dataIndex: "sku", key: "sku", width: 160 },
      { title: "CSV Label", dataIndex: "csvLabel", key: "csvLabel", ellipsis: true },
      {
        title: "FTZ Available (CSV)",
        dataIndex: "csvFtzAvailable",
        key: "csvFtzAvailable",
        width: 140,
        render: (v: number | null) => v ?? "-",
      },
      {
        title: "Onsite Available (CSV)",
        dataIndex: "csvOnsiteAvailable",
        key: "csvOnsiteAvailable",
        width: 150,
        render: (v: number | null) => v ?? "-",
      },
      {
        title: "Status",
        dataIndex: "reason",
        key: "reason",
        render: () => <Tag color="default">Not part of FTZ and Onsite</Tag>,
      },
    ],
    []
  );

  const handleReview = async () => {
    if (fileList.length === 0) {
      toastText("Please select a file", "error");
      return;
    }
    const file = (fileList[0].originFileObj || fileList[0]) as File;
    if (!file) {
      toastText("File object is missing", "error");
      return;
    }

    setReviewing(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await postApi("/items/review-site-qty", formData, true);
      const data = response?.data?.data as ReviewResult;
      setResult(data);
      toastText("Site quantity review completed", "success");
    } catch (error: any) {
      toastText(
        error?.response?.data?.message || "Failed to review site quantity file",
        "error"
      );
    } finally {
      setReviewing(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setResult(null);
    onClose();
  };

  return (
    <Modal
      title="Review QTY (FTZ / Onsite)"
      open={isOpen}
      onCancel={handleCancel}
      centered
      destroyOnClose
      maskClosable={false}
      width={1100}
      footer={[
        <Button key="close" onClick={handleCancel}>
          Close
        </Button>,
        <Button
          key="review"
          type="primary"
          loading={reviewing}
          onClick={handleReview}
          disabled={fileList.length === 0}
        >
          Review File
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Upload QBD inventory report (CSV / Excel)"
        description="Uses the Available column. Matched items with different qty are listed as mismatches. Item names not found in inventory are treated as not part of FTZ and Onsite."
      />

      <Dragger
        multiple={false}
        maxCount={1}
        accept=".csv,.xlsx,.xls"
        fileList={fileList}
        beforeUpload={(file) => {
          setFileList([file as UploadFile]);
          setResult(null);
          return false;
        }}
        onRemove={() => {
          setFileList([]);
          setResult(null);
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area</p>
        <p className="ant-upload-hint">.csv, .xlsx, .xls — max 5 MB</p>
      </Dragger>

      {result && (
        <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: 20 }}>
          <Alert
            type="success"
            showIcon
            message={
              <span>
                Unique items: <b>{result.summary?.uniqueItems ?? 0}</b>
                {" · "}Matched: <b>{result.summary?.matchedInDatabase ?? 0}</b>
                {" · "}Mismatched qty:{" "}
                <b>{result.summary?.mismatchedQtyCount ?? 0}</b>
                {" · "}Not part of FTZ/Onsite:{" "}
                <b>{result.summary?.notPartOfFtzOnsiteCount ?? 0}</b>
                {" · "}Unchanged: <b>{result.summary?.unchanged ?? 0}</b>
              </span>
            }
          />

          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              Mismatched QTY ({result.mismatchedQty?.length ?? 0})
            </Title>
            <Text type="secondary">
              Items found in inventory where CSV Available differs from current FTZ/Onsite.
            </Text>
            <Table
              style={{ marginTop: 8 }}
              size="small"
              rowKey="sku"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              dataSource={result.mismatchedQty || []}
              columns={mismatchColumns}
              scroll={{ x: 900 }}
              locale={{ emptyText: "No mismatched quantities" }}
            />
          </div>

          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              Not part of FTZ and Onsite ({result.notPartOfFtzOnsite?.length ?? 0})
            </Title>
            <Text type="secondary">
              Item name / SKU from the file did not match inventory — treated as not part of
              FTZ and Onsite.
            </Text>
            <Table
              style={{ marginTop: 8 }}
              size="small"
              rowKey="sku"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              dataSource={result.notPartOfFtzOnsite || []}
              columns={notPartColumns}
              scroll={{ x: 800 }}
              locale={{ emptyText: "All CSV items matched inventory" }}
            />
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default SiteQtyReviewModal;
